import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

/* ── Keyframe injection ── */
const injectStyles = () => {
  if (document.getElementById('contact-form-styles')) return;
  const style = document.createElement('style');
  style.id = 'contact-form-styles';
  style.textContent = `
    @keyframes cfShake {
      0%,100% { transform: translateX(0); }
      15%      { transform: translateX(-8px); }
      30%      { transform: translateX(8px); }
      45%      { transform: translateX(-6px); }
      60%      { transform: translateX(6px); }
      75%      { transform: translateX(-3px); }
      90%      { transform: translateX(3px); }
    }
    @keyframes cfToastIn {
      from { transform: translateY(100px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    @keyframes cfToastOut {
      from { transform: translateY(0);    opacity: 1; }
      to   { transform: translateY(100px); opacity: 0; }
    }
    @keyframes cfCheckDraw {
      from { stroke-dashoffset: 60; }
      to   { stroke-dashoffset: 0; }
    }
    @keyframes cfErrorFade {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes cfValidPop {
      0%   { transform: scale(0); opacity: 0; }
      60%  { transform: scale(1.3); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    .cf-input-spring {
      transition: border-color 280ms ease, background 280ms ease, transform 360ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 360ms cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    }
    .cf-input-spring:focus {
      transform: translateY(-2px) scale(1.008) !important;
      border-color: rgba(46,196,160,0.6) !important;
      border-bottom-color: #2EC4A0 !important;
      background: rgba(46,196,160,0.04) !important;
      box-shadow: 0 4px 16px rgba(46,196,160,0.12), 0 0 0 2px rgba(46,196,160,0.1), inset 0 1px 3px rgba(0,0,0,0.2) !important;
      outline: none !important;
    }
    .cf-input-spring:not(:focus) {
      transform: translateY(0) scale(1) !important;
    }
    .cf-textarea-spring {
      transition: border-color 280ms ease, background 280ms ease, transform 360ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 360ms cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    }
    .cf-textarea-spring:focus {
      transform: translateY(-2px) scale(1.004) !important;
      border-color: rgba(46,196,160,0.6) !important;
      border-bottom-color: #2EC4A0 !important;
      background: rgba(46,196,160,0.04) !important;
      box-shadow: 0 4px 16px rgba(46,196,160,0.12), 0 0 0 2px rgba(46,196,160,0.1), inset 0 1px 3px rgba(0,0,0,0.2) !important;
      outline: none !important;
    }
    .cf-textarea-spring:not(:focus) {
      transform: translateY(0) scale(1) !important;
    }
  `;
  document.head?.appendChild(style);
};

/* ── Toast ── */
const SuccessToast = ({ visible, onDone }) => {
  const [phase, setPhase] = useState('in'); // 'in' | 'out'

  useEffect(() => {
    if (!visible) return;
    setPhase('in');
    const t = setTimeout(() => setPhase('out'), 3800);
    const t2 = setTimeout(() => onDone?.(), 4400);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '28px',
      right: '28px',
      zIndex: 99999,
      animation: `${phase === 'in' ? 'cfToastSpringIn' : 'cfToastSpringOut'} ${phase === 'in' ? '520ms' : '360ms'} cubic-bezier(0.34,1.56,0.64,1) forwards`,
      pointerEvents: 'none',
      borderRadius: '4px',
      border: '1.5px solid #F5A623',
      animationName: phase === 'in' ? 'cfToastSpringIn, cfAuroraBorderPulse' : 'cfToastSpringOut',
      animationDuration: phase === 'in' ? '520ms, 2.4s' : '360ms',
      animationTimingFunction: phase === 'in' ? 'cubic-bezier(0.34,1.56,0.64,1), ease-in-out' : 'cubic-bezier(0.34,1.56,0.64,1)',
      animationFillMode: 'forwards, none',
      animationIterationCount: phase === 'in' ? '1, infinite' : '1',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(10,18,32,0.97) 0%, rgba(15,22,38,0.97) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: '16px 22px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        minWidth: '280px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Aurora top stripe */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
          background: 'linear-gradient(90deg, #F5A623 0%, #2EC4A0 50%, #F5A623 100%)',
          backgroundSize: '200% 100%',
          animation: 'cfAuroraShift 2.4s ease-in-out infinite',
        }} />
        {/* Animated checkmark */}
        <div style={{
          width: '34px', height: '34px',
          background: 'rgba(46,196,160,0.12)',
          border: '1.5px solid rgba(46,196,160,0.4)',
          borderRadius: '3px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          animation: 'cfValidPop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.12s both',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <polyline
              points="4,12 9,17 20,6"
              stroke="#2EC4A0"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="60"
              strokeDashoffset="0"
              style={{ animation: 'cfCheckDraw 0.45s ease 0.25s both' }}
            />
          </svg>
        </div>
        <div>
          <div style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: '0.8125rem',
            fontWeight: 700,
            color: '#EEF0F2',
            marginBottom: '3px',
          }}>MELDING SENDT</div>
          <div style={{
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: '0.8rem',
            color: 'rgba(238,240,242,0.6)',
          }}>Vi svarer deg innen 1–2 virkedager.</div>
        </div>
        <div style={{
          width: '6px', height: '6px',
          background: '#F5A623',
          borderRadius: '50%',
          flexShrink: 0,
          marginLeft: 'auto',
          animation: 'cfAuroraBorderPulse 2.4s ease-in-out infinite',
        }} />
      </div>
    </div>
  );
};

/* ── Success Modal ── */
const SuccessModal = ({ form, submittedAt, onClose }) => {
  const timeStr = submittedAt
    ? submittedAt?.toLocaleString('nb-NO', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : '';

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(5,10,18,0.88)',
      zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{
        background: '#0A1220',
        border: '1px solid rgba(59,111,212,0.35)',
        borderTop: '4px solid #E8941A',
        maxWidth: '520px', width: '100%',
        padding: '48px 44px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: '-10px', right: '-10px',
          fontFamily: '"Arial Black", sans-serif',
          fontSize: '9rem', fontWeight: 900,
          color: 'rgba(59,111,212,0.06)',
          lineHeight: 1, userSelect: 'none', pointerEvents: 'none'
        }}>✓</div>
        <div style={{
          position: 'absolute', top: 0, left: 0,
          width: '4px', height: '100%',
          background: 'linear-gradient(180deg, #E8941A 0%, #3B6FD4 100%)'
        }} />
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '16px', right: '16px',
            background: 'none', border: 'none',
            color: 'rgba(238,240,242,0.35)', cursor: 'pointer',
            fontFamily: 'monospace', fontSize: '1rem',
            letterSpacing: '0.05em', padding: '4px 8px',
            transition: 'color 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#EEF0F2'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(238,240,242,0.35)'}
        >✕</button>
        <div style={{
          fontFamily: 'monospace', fontSize: '0.55rem',
          letterSpacing: '0.3em', color: '#E8941A',
          textTransform: 'uppercase', marginBottom: '20px',
          paddingLeft: '16px'
        }}>MELDING BEKREFTET</div>
        <div style={{
          fontFamily: '"Arial Black", sans-serif',
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: 900, color: '#EEF0F2',
          letterSpacing: '-0.04em', textTransform: 'uppercase',
          lineHeight: 0.9, marginBottom: '6px', paddingLeft: '16px'
        }}>SENDT</div>
        <div style={{
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          fontSize: '1.2rem', color: '#3B6FD4',
          marginBottom: '32px', paddingLeft: '16px'
        }}>og mottatt.</div>
        <div style={{
          background: 'rgba(59,111,212,0.07)',
          border: '1px solid rgba(59,111,212,0.18)',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex', alignItems: 'center', gap: '12px'
        }}>
          <div style={{ width: '6px', height: '6px', background: '#3B6FD4', borderRadius: '50%', flexShrink: 0 }} />
          <div>
            <div style={{
              fontFamily: 'monospace', fontSize: '0.55rem',
              letterSpacing: '0.2em', color: 'rgba(238,240,242,0.35)',
              textTransform: 'uppercase', marginBottom: '2px'
            }}>INNSENDT TIDSPUNKT</div>
            <div style={{
              fontFamily: 'monospace', fontSize: '0.8rem',
              color: '#EEF0F2', letterSpacing: '0.05em'
            }}>{timeStr}</div>
          </div>
        </div>
        <div style={{
          background: 'rgba(232,148,26,0.05)',
          border: '1px solid rgba(232,148,26,0.18)',
          padding: '16px 20px',
          marginBottom: '32px'
        }}>
          <div style={{
            fontFamily: 'monospace', fontSize: '0.55rem',
            letterSpacing: '0.25em', color: '#E8941A',
            textTransform: 'uppercase', marginBottom: '14px'
          }}>SAMTYKKE-STATUS</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <div style={{
              width: '18px', height: '18px',
              border: `1.5px solid ${form?.optinEmail ? '#3B6FD4' : 'rgba(238,240,242,0.2)'}`,
              background: form?.optinEmail ? 'rgba(59,111,212,0.25)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              {form?.optinEmail && <span style={{ color: '#3B6FD4', fontSize: '0.7rem', fontWeight: 900 }}>✓</span>}
            </div>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#EEF0F2', textTransform: 'uppercase' }}>E-POST NYHETSBREV</div>
              <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.72rem', color: 'rgba(238,240,242,0.45)', marginTop: '1px' }}>
                {form?.optinEmail ? 'Samtykke gitt — du vil motta e-post fra oss' : 'Ikke valgt'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '18px', height: '18px',
              border: `1.5px solid ${form?.optinSms ? '#E8941A' : 'rgba(238,240,242,0.2)'}`,
              background: form?.optinSms ? 'rgba(232,148,26,0.15)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              {form?.optinSms && <span style={{ color: '#E8941A', fontSize: '0.7rem', fontWeight: 900 }}>✓</span>}
            </div>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#EEF0F2', textTransform: 'uppercase' }}>SMS-VARSLER</div>
              <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.72rem', color: 'rgba(238,240,242,0.45)', marginTop: '1px' }}>
                {form?.optinSms ? 'Samtykke gitt — du vil motta SMS-oppdateringer' : 'Ikke valgt'}
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <a
            href="/nord-pass-subscription-portal"
            style={{
              background: '#E8941A', color: '#050A12', border: 'none',
              padding: '14px 28px',
              fontFamily: '"Arial Black", sans-serif', fontSize: '0.75rem',
              fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase',
              cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
              clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
            }}
          >SE DITT ABONNEMENT →</a>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', color: 'rgba(238,240,242,0.5)',
              border: '1px solid rgba(238,240,242,0.15)',
              padding: '14px 24px', fontFamily: 'monospace',
              fontSize: '0.65rem', letterSpacing: '0.15em',
              textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s'
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#EEF0F2'; e.currentTarget.style.borderColor = 'rgba(238,240,242,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(238,240,242,0.5)'; e.currentTarget.style.borderColor = 'rgba(238,240,242,0.15)'; }}
          >LUKK</button>
        </div>
        <div style={{
          position: 'absolute', bottom: '16px', right: '20px',
          border: '2px solid rgba(232,148,26,0.25)', padding: '3px 10px',
          transform: 'rotate(-2deg)',
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          fontSize: '0.65rem', color: 'rgba(232,148,26,0.4)', pointerEvents: 'none'
        }}>vi sees snart ✦</div>
      </div>
    </div>
  );
};

/* ── Validation helpers ── */
const PHONE_RE = /^(\+47[\s]?)?[0-9]{2}[\s]?[0-9]{2}[\s]?[0-9]{2}[\s]?[0-9]{2}$|^\+?[0-9]{7,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_MSG = 20;

const validateField = (field, value) => {
  switch (field) {
    case 'name':
      if (!value?.trim()) return 'Navn er påkrevd';
      if (value?.trim()?.length < 2) return 'Navn må ha minst 2 tegn';
      return null;
    case 'email':
      if (!value?.trim()) return 'E-postadresse er påkrevd';
      if (!EMAIL_RE?.test(value?.trim())) return 'Ugyldig e-postformat (f.eks. ola@eksempel.no)';
      return null;
    case 'phone':
      if (value?.trim() && !PHONE_RE?.test(value?.trim())) return 'Ugyldig telefonnummer (f.eks. +47 000 00 000)';
      return null;
    case 'message':
      if (!value?.trim()) return 'Melding er påkrevd';
      if (value?.trim()?.length < MIN_MSG) return `Meldingen må ha minst ${MIN_MSG} tegn (${value?.trim()?.length}/${MIN_MSG})`;
      return null;
    default:
      return null;
  }
};

/* ── Valid checkmark icon ── */
const ValidMark = () => (
  <div style={{
    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
    animation: 'cfValidPop 0.3s cubic-bezier(0.34,1.56,0.64,1) both'
  }}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="rgba(59,111,212,0.15)" stroke="#3B6FD4" strokeWidth="1.5" />
      <polyline
        points="7,12 10.5,15.5 17,8"
        stroke="#3B6FD4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray="20" strokeDashoffset="0"
        style={{ animation: 'cfCheckDraw 0.35s ease 0.1s both' }}
      />
    </svg>
  </div>
);

/* ── Error message ── */
const FieldError = ({ msg }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '6px',
    marginTop: '6px',
    animation: 'cfErrorFade 0.2s ease both'
  }}>
    <div style={{
      width: '3px', height: '3px',
      background: '#E8941A',
      borderRadius: '50%', flexShrink: 0
    }} />
    <span style={{
      fontFamily: 'monospace',
      fontSize: '0.58rem',
      letterSpacing: '0.08em',
      color: '#E8941A',
      lineHeight: 1.4
    }}>{msg}</span>
  </div>
);

/* ── Main component ── */
const ContactForm = () => {
  useEffect(() => { injectStyles(); }, []);

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', optinEmail: false, optinSms: false });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [valid, setValid] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [submittedForm, setSubmittedForm] = useState(null);
  const [sending, setSending] = useState(false);
  const [shake, setShake] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const formRef = useRef(null);

  /* Blur validation */
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const err = validateField(field, form?.[field]);
    setErrors(prev => ({ ...prev, [field]: err }));
    setValid(prev => ({ ...prev, [field]: !err && form?.[field]?.trim?.() !== '' }));
  };

  /* Change handler */
  const handleChange = (field, val) => {
    setForm(prev => ({ ...prev, [field]: val }));
    if (touched?.[field]) {
      const err = validateField(field, val);
      setErrors(prev => ({ ...prev, [field]: err }));
      setValid(prev => ({ ...prev, [field]: !err && (typeof val === 'string' ? val?.trim() !== '' : true) }));
    }
  };

  /* Full validation on submit */
  const validateAll = () => {
    const fields = ['name', 'email', 'phone', 'message'];
    const newErrors = {};
    const newValid = {};
    fields?.forEach(f => {
      const err = validateField(f, form?.[f]);
      if (err) newErrors[f] = err;
      else if (form?.[f]?.trim?.()) newValid[f] = true;
    });
    setErrors(newErrors);
    setValid(newValid);
    setTouched({ name: true, email: true, phone: true, message: true });
    return newErrors;
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault();
    const errs = validateAll();
    if (Object.keys(errs)?.length) {
      setShake(true);
      setTimeout(() => setShake(false), 700);
      return;
    }
    setSending(true);
    try {
      const { error: dbError } = await supabase?.from('contact_submissions')?.insert({
        name: form?.name?.trim(),
        email: form?.email?.trim(),
        phone: form?.phone?.trim() || null,
        message: form?.message?.trim(),
        optin_email: form?.optinEmail,
        optin_sms: form?.optinSms
      });
      if (dbError) throw dbError;
      if (form?.optinEmail || form?.optinSms) {
        await supabase?.from('contact_optins')?.insert({
          email: form?.optinEmail ? form?.email?.trim() : null,
          phone: form?.optinSms && form?.phone?.trim() ? form?.phone?.trim() : null,
          optin_email: form?.optinEmail,
          optin_sms: form?.optinSms,
          source: 'contact_form'
        });
      }
    } catch (err) {
      console.error('Contact form submission error:', err);
    } finally {
      setSending(false);
      setSubmittedAt(new Date());
      setSubmittedForm({ ...form });
      setSubmitted(true);
      setShowToast(true);
    }
  };

  const handleCloseModal = () => {
    setSubmitted(false);
    setForm({ name: '', email: '', phone: '', message: '', optinEmail: false, optinSms: false });
    setErrors({});
    setTouched({});
    setValid({});
  };

  /* Input style with validation state */
  const inputStyle = (field, isTextarea = false) => {
    const hasError = !!errors?.[field];
    const isValid = valid?.[field];
    return {
      width: '100%',
      background: hasError
        ? 'rgba(232,148,26,0.04)'
        : isValid
          ? 'rgba(59,111,212,0.06)'
          : 'rgba(255,255,255,0.04)',
      border: `1px solid ${
        hasError ? 'rgba(232,148,26,0.6)'
        : isValid ? 'rgba(59,111,212,0.5)' :'rgba(255,255,255,0.12)'
      }`,
      borderBottom: `2px solid ${
        hasError ? '#E8941A' : isValid ?'#3B6FD4' :'#3B6FD4'
      }`,
      color: '#EEF0F2',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      fontSize: '0.95rem',
      padding: '14px 16px',
      paddingRight: isValid && !isTextarea ? '40px' : '16px',
      outline: 'none',
      transition: 'border-color 0.2s, background 0.2s',
      boxSizing: 'border-box',
      borderRadius: '0',
      resize: isTextarea ? 'vertical' : undefined
    };
  };

  const labelStyle = {
    fontFamily: 'monospace',
    fontSize: '0.6rem',
    letterSpacing: '0.25em',
    color: '#E8941A',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '8px'
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Success Modal */}
      {submitted && (
        <SuccessModal form={submittedForm} submittedAt={submittedAt} onClose={handleCloseModal} />
      )}
      {/* Success Toast */}
      <SuccessToast visible={showToast} onDone={() => setShowToast(false)} />
      {/* Section label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '36px' }}>
        <div style={{ width: '28px', height: '1px', background: '#E8941A' }} />
        <span style={{
          fontFamily: 'monospace', fontSize: '0.6rem',
          letterSpacing: '0.3em', color: '#E8941A', textTransform: 'uppercase'
        }}>Send oss en melding</span>
      </div>
      {/* Ghost number */}
      <div style={{
        position: 'absolute', top: '-30px', right: '-10px',
        fontFamily: '"Arial Black", sans-serif',
        fontSize: '9rem', fontWeight: 900,
        color: 'rgba(59,111,212,0.05)',
        lineHeight: 1, userSelect: 'none', zIndex: 0
      }}>01</div>
      <div style={{
        fontFamily: '"Arial Black", sans-serif',
        fontSize: 'clamp(2rem, 4vw, 3.5rem)',
        fontWeight: 900, color: '#EEF0F2',
        letterSpacing: '-0.04em', textTransform: 'uppercase',
        lineHeight: 0.9, marginBottom: '8px', position: 'relative', zIndex: 1
      }}>SKRIV TIL</div>
      <div style={{
        fontFamily: 'Georgia, serif', fontStyle: 'italic',
        fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
        color: '#3B6FD4', lineHeight: 1, marginBottom: '40px',
        position: 'relative', zIndex: 1
      }}>oss direkte.</div>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        style={{
          position: 'relative', zIndex: 1,
          animation: shake ? 'cfShake 0.6s ease both' : 'none'
        }}
      >
        {/* Name + Phone row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          {/* Name */}
          <div>
            <label style={labelStyle}>Fullt navn *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={form?.name}
                onChange={e => handleChange('name', e?.target?.value)}
                onBlur={() => handleBlur('name')}
                placeholder="Ola Nordmann"
                style={inputStyle('name')}
                className="cf-input-spring"
              />
              {valid?.name && <ValidMark />}
            </div>
            {errors?.name && <FieldError msg={errors?.name} />}
          </div>

          {/* Phone */}
          <div>
            <label style={labelStyle}>Telefon</label>
            <div style={{ position: 'relative' }}>
              <input
                type="tel"
                value={form?.phone}
                onChange={e => handleChange('phone', e?.target?.value)}
                onBlur={() => handleBlur('phone')}
                placeholder="+47 000 00 000"
                style={inputStyle('phone')}
                className="cf-input-spring"
              />
              {valid?.phone && <ValidMark />}
            </div>
            {errors?.phone && <FieldError msg={errors?.phone} />}
          </div>
        </div>

        {/* Email */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>E-postadresse *</label>
          <div style={{ position: 'relative' }}>
            <input
              type="email"
              value={form?.email}
              onChange={e => handleChange('email', e?.target?.value)}
              onBlur={() => handleBlur('email')}
              placeholder="ola@eksempel.no"
              style={inputStyle('email')}
              className="cf-input-spring"
            />
            {valid?.email && <ValidMark />}
          </div>
          {errors?.email && <FieldError msg={errors?.email} />}
        </div>

        {/* Message */}
        <div style={{ marginBottom: '28px' }}>
          <label style={{
            ...labelStyle,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span>Din melding *</span>
            <span style={{
              color: form?.message?.trim()?.length >= MIN_MSG ? '#3B6FD4' : 'rgba(238,240,242,0.25)',
              transition: 'color 0.2s'
            }}>{form?.message?.trim()?.length || 0}/{MIN_MSG}</span>
          </label>
          <textarea
            value={form?.message}
            onChange={e => handleChange('message', e?.target?.value)}
            onBlur={() => handleBlur('message')}
            placeholder="Fortell oss hva du lurer p\u00e5..."
            rows={6}
            style={inputStyle('message', true)}
            className="cf-textarea-spring"
          />
          {errors?.message && <FieldError msg={errors?.message} />}
        </div>

        {/* Opt-in checkboxes */}
        <div style={{
          background: 'rgba(59,111,212,0.05)',
          border: '1px solid rgba(59,111,212,0.15)',
          padding: '20px 24px',
          marginBottom: '28px'
        }}>
          <div style={{
            fontFamily: 'monospace', fontSize: '0.6rem',
            letterSpacing: '0.25em', color: '#E8941A',
            textTransform: 'uppercase', marginBottom: '16px'
          }}>MARKEDSFORINGSSAMTYKKE</div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', marginBottom: '14px' }}>
            <input
              type="checkbox"
              checked={form?.optinEmail}
              onChange={e => handleChange('optinEmail', e?.target?.checked)}
              style={{ marginTop: '2px', accentColor: '#3B6FD4', width: '15px', height: '15px', flexShrink: 0, cursor: 'pointer' }}
            />
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.82rem', color: 'rgba(238,240,242,0.5)', lineHeight: 1.5 }}>
              Ja, jeg vil motta nyhetsbrev og e-posttilbud fra NordPass.
            </span>
          </label>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form?.optinSms}
              onChange={e => handleChange('optinSms', e?.target?.checked)}
              style={{ marginTop: '2px', accentColor: '#E8941A', width: '15px', height: '15px', flexShrink: 0, cursor: 'pointer' }}
            />
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.82rem', color: 'rgba(238,240,242,0.5)', lineHeight: 1.5 }}>
              Ja, jeg vil motta SMS-varsler og tilbud fra NordPass. (Krever telefonnummer)
            </span>
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={sending}
          style={{
            background: sending ? 'rgba(59,111,212,0.5)' : '#3B6FD4',
            color: '#EEF0F2',
            border: 'none',
            padding: '16px 40px',
            fontFamily: '"Arial Black", sans-serif',
            fontSize: '0.85rem',
            fontWeight: 900,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: sending ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
          }}
          className={sending ? '' : 'btn-spring btn-aurora'}
        >
          {sending ? 'Sender...' : 'Send melding →'}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
